/**
 * Track B view model (INC-1007, UI half): grouping by horizon, labels and source text. Pure.
 * Owning vertical: Track B (read-only; Track B never trades).
 */
import { shortHash } from "../ops-common.ts";
import { TRACKB_HORIZONS, type Horizon, type Source, type TrackbRow } from "./contract.ts";

export const HORIZON_LABELS: Readonly<Record<Horizon, string>> = {
  VERY_SHORT: "Very short horizon",
  SHORT: "Short horizon",
  MEDIUM: "Medium horizon",
  LONG: "Long horizon",
  VERY_LONG: "Very long horizon",
};

export interface HorizonGroup {
  readonly horizon: Horizon;
  readonly label: string;
  readonly rows: readonly TrackbRow[];
}

/** Group cards by horizon in horizon order, best rank first (a name without a rank last); empty horizons are omitted. */
export function groupByHorizon(rows: readonly TrackbRow[]): readonly HorizonGroup[] {
  return TRACKB_HORIZONS.map((horizon) => ({
    horizon,
    label: HORIZON_LABELS[horizon],
    rows: rows
      .filter((row) => row.horizon === horizon)
      .sort(
        (a, b) =>
          (a.factors.rank ?? Number.MAX_SAFE_INTEGER) -
            (b.factors.rank ?? Number.MAX_SAFE_INTEGER) || a.isin.localeCompare(b.isin),
      ),
  })).filter((group) => group.rows.length > 0);
}

/** `role hash12, role hash12`, or a statement that nothing was used (never a blank). */
export function sourcesText(sources: readonly Source[]): string {
  return sources.length === 0
    ? "none used"
    : sources.map((source) => `${source.role} ${shortHash(source.manifestHash)}`).join(", ");
}

/** The words for a reason code (`DATA_MISSING` -> `data missing`); the code itself is shown beside it. */
export function reasonText(code: string): string {
  return code.toLowerCase().replace(/_/g, " ");
}
